import { Box, Typography, Button, Container, Paper, Alert } from "@mui/material";
import EmailIcon from "@mui/icons-material/Email";
import GoogleIcon from "@mui/icons-material/Google";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { ROUTES } from "../routes/routes";
import axiosInstance from "../features/axiosInstance/axiosInstance";

const EmailConfirmationPage = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(0);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    if (location.state?.email) {
      localStorage.setItem("pendingConfirmationEmail", location.state.email);
    } else {
      const storedEmail = localStorage.getItem("pendingConfirmationEmail");
      if (!storedEmail) {
        navigate(ROUTES.LOGIN);
      }
    }
  }, [location.state, navigate]);

  const userEmail =
    location.state?.email ||
    localStorage.getItem("pendingConfirmationEmail") ||
    "your@email.com";

  const handleOpenGmail = () => {
    window.open("https://mail.google.com", "_blank");
  };

  const handleOpenOutlook = () => {
    window.open("https://outlook.office.com/mail/", "_blank");
  };

  const handleResendEmail = async () => {
    const email = userEmail;
    setIsResending(true);
    setErrorMessage("");
    try {
      await axiosInstance.post("account/resend-confirmation-email", {
        email: email,
      });
      setTimer(30);
      const countdown = setInterval(() => {
        setTimer((prev) => {
          if (prev <= 1) {
            clearInterval(countdown);
            setIsResending(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (error) {
      setErrorMessage("There was an error resending the email.");
      setIsResending(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: `calc(100vh - var(--header-height, 72px))`,
        }}
      >
        <Paper
          elevation={0}
          sx={{ p: { xs: 3, sm: 5 }, width: "100%" }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(14,134,212,0.08) 0%, rgba(26,155,230,0.08) 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EmailIcon sx={{ width: 40, height: 40, color: "primary.main" }} />
            </Box>
          </Box>

          <Typography
            variant="h5"
            component="h1"
            align="center"
            gutterBottom
          >
            We've sent you a confirmation email
          </Typography>

          <Typography variant="body1" align="center" sx={{ mb: 3, color: "text.secondary" }}>
            To log in to your account you'll need to verify your email:
            <Box
              component="span"
              sx={{
                ml: 1,
                backgroundColor: "rgba(14,134,212,0.06)",
                color: "primary.main",
                fontWeight: 500,
                px: 1.5,
                py: 0.5,
                borderRadius: 2,
                display: "inline-block",
                textAlign: "center",
              }}
            >
              {userEmail}
            </Box>
          </Typography>

          <Box
            sx={{ display: "flex", justifyContent: "center", gap: 2, mb: 3 }}
          >
            <Button
              variant="outlined"
              onClick={handleOpenGmail}
              startIcon={<GoogleIcon />}
              sx={{ px: 3 }}
            >
              Open Gmail
            </Button>
            <Button
              variant="outlined"
              onClick={handleOpenOutlook}
              sx={{ px: 3 }}
            >
              Open Outlook
            </Button>
          </Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            {isResending && timer > 0 && (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {timer} seconds
              </Typography>
            )}
            <Button
              variant="outlined"
              onClick={handleResendEmail}
              disabled={isResending}
              sx={{ px: 3 }}
            >
              Resend Email
            </Button>
          </Box>
          {errorMessage && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errorMessage}
            </Alert>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default EmailConfirmationPage;
